import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Settings from './components/Settings';
import AdminPanel from './components/AdminPanel';
import DisplayView from './components/DisplayView';
import TodoApp from './apps/TodoApp';
import TimeOffApp from './apps/TimeOffApp';
import ProductLocationsApp from './apps/ProductLocationsApp';
import CycleCountApp from './apps/CycleCountApp';
import PlannerApp from './apps/PlannerApp';
import DashboardApp from './apps/DashboardApp';
import KnowledgeBaseApp from './apps/KnowledgeBaseApp';
import FactoryMapsApp from './apps/FactoryMapsApp';
import LabelsApp from './apps/LabelsApp';
import LabelsAppTest from './apps/LabelsAppTest';
import BinAuditApp from './apps/BinAuditApp';
import PublicProductLocations from './apps/PublicProductLocations';
import LoadingScreen from './components/LoadingScreen';
import DisplayPage from './pages/DisplayPage';

// Import individual display pages
import NotesDisplay from './pages/display/NotesDisplay';
import TimeOffDisplay from './pages/display/TimeOffDisplay';
import CycleCountDisplay from './pages/display/CycleCountDisplay';
import TasksDisplay from './pages/display/TasksDisplay';

// Import the public display wrapper
import PublicDisplayWrapper from './components/PublicDisplayWrapper';

// Create public display components with hardcoded token
const PublicTimeOffDisplay = () => <TimeOffDisplay isPublic={true} hardcodedToken="display123" />;
const PublicCycleCountDisplay = () => <CycleCountDisplay isPublic={true} hardcodedToken="display123" />;
const PublicNotesDisplay = () => <NotesDisplay isPublic={true} hardcodedToken="display123" />;
const PublicTasksDisplay = () => <TasksDisplay isPublic={true} hardcodedToken="display123" />;
const PublicDisplayPageDirect = () => <DisplayPage isPublic={true} hardcodedToken="display123" />;

function App() {
  const { isAuthenticated, token, loading } = useAuth();
  
  // Add some debugging
  console.log('App rendering, auth state:', { isAuthenticated, hasToken: !!token, loading });

  // Show loading screen while checking authentication
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" />} />
      <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/" />} />
      
      {/* Digital Signage Display Routes (Protected) */}
      <Route path="/display" element={<DisplayPage />} />
      <Route path="/display/notes" element={<NotesDisplay />} />
      <Route path="/display/timeoff" element={<TimeOffDisplay />} />
      <Route path="/display/inventory" element={<CycleCountDisplay />} />
      <Route path="/display/tasks" element={<TasksDisplay />} />
      
      {/* Public Digital Signage Display Routes (with simple token protection) */}
      <Route path="/public-display/:displayToken" element={<PublicDisplayWrapper />}>
        <Route index element={<DisplayPage isPublic={true} />} />
        <Route path="notes" element={<NotesDisplay isPublic={true} />} />
        <Route path="timeoff" element={<TimeOffDisplay isPublic={true} />} />
        <Route path="inventory" element={<CycleCountDisplay isPublic={true} />} />
        <Route path="tasks" element={<TasksDisplay isPublic={true} />} />
      </Route>
      
      {/* Direct public routes for digital signage (similar to /locations) */}
      <Route path="/public/productlocations" element={<PublicProductLocations />} />
      <Route path="/locations" element={<PublicProductLocations />} />
      <Route path="/timeoff-display" element={<PublicTimeOffDisplay />} />
      <Route path="/inventory-display" element={<PublicCycleCountDisplay />} />
      <Route path="/notes-display" element={<PublicNotesDisplay />} />
      <Route path="/tasks-display" element={<PublicTasksDisplay />} />
      <Route path="/dashboard-display" element={<PublicDisplayPageDirect />} />
      
      {isAuthenticated ? (
        <Route path="/" element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="settings" element={<Settings />} />
          <Route path="admin" element={<AdminPanel />} />
          <Route path="todo" element={<TodoApp />} />
          <Route path="timeoff" element={<TimeOffApp />} />
          <Route path="productlocations" element={<ProductLocationsApp />} />
          <Route path="cyclecount" element={<CycleCountApp />} />
          <Route path="planner" element={<PlannerApp />} />
          <Route path="dashboard" element={<DashboardApp />} />
          <Route path="knowledgebase" element={<KnowledgeBaseApp />} />
          <Route path="factorymaps" element={<FactoryMapsApp />} />
          <Route path="labels" element={<LabelsApp />} />
          <Route path="binaudit" element={<BinAuditApp />} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/login" />} />
      )}
    </Routes>
  );
}

export default App; 